<template>
  <select
    v-bind="$attrs"
    data-el="form-select"
    :size="hasSize"
    :required="required"
    :disabled="disabled || loading"
    :class="{
      'z-10 border-0 border-transparent': inGroup,
      'rounded-md border-gray-300': !inGroup,
      'block focus:z-10 focus:ring-4 focus:ring-blue-300': true,
      'w-full': !inline,
      'shadow-sm': !noShadow && !inGroup,
      'text-md': size !== 'xs',
      'text-sm': size === 'xs',
      'py-3 pl-4': size === 'md',
      'pr-4': size === 'md' && hasSize,
      'py-2 pl-3': size === 'sm',
      'py-1 pl-2': size === 'xs',
      'pr-3': size === 'sm' && hasSize,
      // 'border rounded border-gray-400 focus:ring-blue-300 focus:ring-4 focus:outline-none': true,
      'bg-gray-50 focus:ring-gray-300': disabled,
    }"
    :value="modelValue || value"
    @change="selectChange"
  >
    <option v-if="loading" disabled selected hidden value="">Loading...</option>
    <option v-else-if="placeholder" disabled selected hidden value="">
      {{ placeholder }}
    </option>
    <optgroup
      v-for="optionGroup in loading ? [] : optionGroups"
      :key="optionGroup.text"
      :label="optionGroup.text"
    >
      <option
        v-for="option in optionGroup.options"
        v-bind="option.dataMap"
        :id="option.id"
        :key="option.value"
        :disabled="option.disabled"
        :value="option.value"
        :selected="option.value === value"
      >
        {{ truncate(option.text) }}
      </option>
    </optgroup>
    <option
      v-for="option in loading ? [] : options"
      v-bind="option.dataMap"
      :key="option.value"
      :disabled="option.disabled"
      :value="option.value"
      :selected="option.value === value"
    >
      {{ truncate(option.text) }}
    </option>
  </select>
</template>
<script lang="ts" setup generic="T extends string">
import { computed } from 'vue';
import { truncate } from '~/lib/services/helpers';
import type {
  FormSelectOption,
  FormSelectOptionGroup,
  FormSize,
} from '~/lib/services/types';
import { getLogger } from '~/lib/services/getLogger';

const log = getLogger('FormSelect');
const props = withDefaults(
  defineProps<{
    inGroup?: boolean;
    options?: FormSelectOption<T>[];
    optionGroups?: FormSelectOptionGroup<T>[];
    modelValue?: T | undefined | null;
    value?: T | undefined | null;
    size?: FormSize;
    required?: boolean;
    disabled?: boolean;
    showAll?: boolean;
    noShadow?: boolean;
    inline?: boolean;
    placeholder?: string;
    loading?: boolean;
  }>(),
  {
    options: () => [],
    optionGroups: () => [],
    inGroup: false,
    loading: false,
    showAll: false,
    value: undefined,
    modelValue: undefined,
    size: 'md',
    noShadow: false,
    required: false,
    disabled: undefined,
    inline: undefined,
    placeholder: undefined,
  },
);
const emit = defineEmits<{
  (e: 'change' | 'update:modelValue', p: T): void;
}>();

const selectChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  log('Select change: ' + target.value);
  emit('update:modelValue', target.value as T);
  emit('change', target.value as T);
};
const hasSize = computed(() =>
  props.showAll ? props.options.length || props.optionGroups.length : undefined,
);
defineOptions({
  inheritAttrs: false,
});
</script>
